import React, { Suspense, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { flowRight } from 'lodash';
import { type WithTranslation } from '@wix/yoshi-flow-editor';
import { PlayArrow } from '../../../common/components/icons/play-arrow';
import { PlayIcon } from '../../../common/components/icons/play-icon';
import ImageLoader from '../../../common/components/image-loader';
import withDeviceType, { type WithDeviceType } from '../../../common/hoc/with-device-type';
import withEventEmitter, { type WithEventEmitter } from '../../../common/hoc/with-event-emitter';
import withMediaHosts, { type WithMediaHosts } from '../../../common/hoc/with-media-hosts';
import withTranslate from '../../../common/hoc/with-translate';
import { getVideoEmbedThumbUrl } from '../../../common/services/get-video-embed-thumb-url';
import { buildUrl } from '../../../common/services/video-embed';
import styles from './video-embed.scss';

const ReactPlayer = React.lazy(
  () => import(/* webpackChunkName: "react-player" */ './react-player-wrapper'),
);

const MAX_WAIT_TIME = 5000;

const getStyle = (oembed: Oembed) => ({
  backgroundImage: `url(${getVideoEmbedThumbUrl(oembed)})`,
});

type Oembed = Parameters<typeof getVideoEmbedThumbUrl>[0] & {
  video_url?: string;
};

type OwnProps = {
  oembed: Oembed;
  alt?: string;
  className?: string;
};

type Props = OwnProps &
  WithDeviceType &
  WithEventEmitter &
  WithMediaHosts &
  Pick<WithTranslation, 't'>;

const VideoEmbed: React.FC<Props> = ({
  oembed,
  alt,
  className,
  isMobile,
  isDesktop,
  eventEmitter,
  videoHost,
  t,
}) => {
  const [{ isLoading, isLoaded }, setState] = useState({ isLoading: false, isLoaded: false });
  // Identifies this embed in 'video-start' events so other embeds can reset
  const playerId = useRef({}).current;
  const timeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const handleVideoStart = (id: unknown) => {
      if (id !== playerId) {
        setState({ isLoading: false, isLoaded: false });
      }
    };
    eventEmitter.on('video-start', handleVideoStart);
    return () => {
      eventEmitter.removeListener('video-start', handleVideoStart);
      clearTimeout(timeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReady = () => setState((s) => (s.isLoading ? { ...s, isLoaded: true } : s));

  const handlePlay = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setState((s) => ({ ...s, isLoading: true }));
    timeout.current = setTimeout(handleReady, MAX_WAIT_TIME);
  };

  const handleStart = () => eventEmitter.emitEvent('video-start', [playerId]);

  return (
    <div className={classNames(styles.videoEmbed, className)}>
      {isLoading && (
        <Suspense fallback={null}>
          <ReactPlayer
            className={styles.player}
            width="100%"
            height="100%"
            url={buildUrl(oembed.video_url ?? '', videoHost ?? '')}
            onReady={handleReady}
            onStart={handleStart}
            playing={isDesktop}
            controls
          />
        </Suspense>
      )}
      {!isLoaded && (
        <div
          className={styles.thumbnail}
          style={getStyle(oembed)}
          onClick={handlePlay}
          role="img"
          aria-label={alt}
        >
          <div className={styles.overlay} />
          {!isLoading && isMobile && oembed.video_url && (
            <div className={styles.button}>
              <PlayArrow className={styles.arrow} /> {t('video-embed.load-video')}
            </div>
          )}
          {!isLoading && isDesktop && (
            <span>
              <PlayIcon className={styles.icon} />
            </span>
          )}
          {isLoading && <ImageLoader type="medium" />}
        </div>
      )}
    </div>
  );
};

export default flowRight(
  withTranslate,
  withDeviceType,
  withEventEmitter,
  withMediaHosts,
)(VideoEmbed);
